/**
 * Device capability detection and resource analysis.
 * Provides dynamic timeouts, frame rates, and quality settings based on AVAILABLE hardware.
 *
 * Checks:
 * - Available memory (not just total)
 * - Available CPU percentage
 * - GPU availability and memory
 * - Current system load
 *
 * Supports: Web, Electron iframe (external_api.min.js), Mobile browser
 * Safely handles missing APIs with graceful fallbacks
 */

import logger from '../../virtual-background/logger';

/**
 * Device capability levels
 */
export const DEVICE_CAPABILITY = {
    HIGH: 'high', // 8+ cores, 16GB+ RAM, dedicated GPU
    MEDIUM: 'medium', // 4-6 cores, 8GB RAM, integrated GPU
    LOW: 'low', // 2-4 cores, 4GB RAM, minimal GPU
    VERY_LOW: 'very_low' // 1-2 cores, <4GB RAM, no GPU
};

/**
 * Safely get navigator API with fallback
 * Works in: Web, Electron iframe, Mobile browser
 *
 * @private
 * @param {string} prop - Property name to get from navigator
 * @param {*} fallback - Fallback value if property unavailable
 * @returns {*} Property value or fallback
 */
function safeNavigatorGet(prop, fallback) {
    try {
        // Check if navigator exists (could be missing in some iframe contexts)
        if (typeof navigator === 'undefined' || navigator === null) {
            return fallback;
        }

        // Check if property exists
        if (prop in navigator && navigator[prop] !== null && navigator[prop] !== undefined) {
            return navigator[prop];
        }

        return fallback;
    } catch {
        // In rare cases, accessing navigator properties can throw
        return fallback;
    }
}

/**
 * Get available (free) memory in GB
 * Uses performance.memory if available (Chrome/Edge)
 * Falls back to total device memory otherwise
 *
 * @private
 * @returns {number} Available memory in GB
 */
export function getAvailableMemory() {
    try {
        // Chrome/Edge: performance.memory gives real available memory
        if (typeof performance !== 'undefined' && performance.memory) {
            const availableBytes = performance.memory.jsHeapSizeLimit - performance.memory.totalJSHeapSize;
            const availableGb = availableBytes / (1024 * 1024 * 1024);

            return Math.max(0, availableGb);
        }
    } catch {
        // Performance.memory access failed
    }

    // Fallback to total device memory (assumes most is available)
    return safeNavigatorGet('deviceMemory', 8);
}

/**
 * Get current CPU usage percentage (estimated)
 * Measures how busy the CPU is with a quick benchmark
 *
 * @private
 * @returns {number} Estimated CPU usage 0-100%
 */
export function getEstimatedCpuUsage() {
    try {
        const startTime = performance.now();

        // Run a known workload
        let result = 0;

        for (let i = 0; i < 1000000; i++) {
            result += Math.sqrt(i);
        }

        const elapsedMs = performance.now() - startTime;

        // Baseline on high-end device: ~5ms
        // If taking 10ms: ~50% CPU
        // If taking 20ms: ~100% CPU (CPU is very busy)
        const estimatedCpuUsage = Math.min(100, (elapsedMs / 5) * 50);

        void result; // Use result to prevent optimization

        return estimatedCpuUsage;
    } catch {
        return 50; // Assume moderate CPU usage if detection fails
    }
}

/**
 * Get battery status (mobile devices only)
 * Critical for mobile: low battery severely reduces performance
 *
 * @private
 * @returns {Promise<Object|null>} Battery status or null if not available
 */
async function getBatteryStatus() {
    try {
        // Navigator.getBattery() is deprecated but still supported
        // Use battery API if available (most mobile browsers)
        if (navigator.getBattery) {
            const battery = await navigator.getBattery();

            return {
                level: battery.level, // 0-1 (0% to 100%)
                charging: battery.charging, // boolean
                chargingTime: battery.chargingTime, // seconds
                dischargingTime: battery.dischargingTime // seconds
            };
        }
    } catch {
        // Battery API not available or failed
    }

    // Fallback: Not available on desktop browsers
    return null;
}

/**
 * Detect thermal throttling by comparing CPU benchmark times
 * High-end device baseline: ~5ms
 * Thermally throttled device: ~15ms+ (3× slower)
 *
 * @private
 * @param {number} currentCpuTime - Current CPU benchmark time in ms
 * @param {number} baselineCpuTime - Baseline (high-end device) in ms
 * @returns {boolean} True if thermal throttling detected (>50% slower)
 */
export function detectThermalThrottling(currentCpuTime, baselineCpuTime = 5) {
    // If CPU time is 50% higher than baseline, likely throttled
    // Baseline 5ms → 7.5ms+ indicates throttling
    return currentCpuTime > baselineCpuTime * 1.5;
}

/**
 * Detect if device has graphics acceleration available
 * Safe for all contexts: Web, Electron iframe, Mobile
 *
 * @private
 * @returns {boolean} True if WebGL or Canvas acceleration likely available
 */
function hasGraphicsAcceleration() {
    try {
        // Check if document.createElement is available (should be in all contexts)
        if (typeof document === 'undefined' || !document.createElement) {
            return false;
        }

        const canvas = document.createElement('canvas');

        // Skip check if canvas creation failed
        if (!canvas) {
            return false;
        }

        // Try WebGL 2 first (faster, newer)
        let gl = canvas.getContext('webgl2', { alpha: false });

        if (gl && gl instanceof WebGLRenderingContext) {
            return true;
        }

        // Fallback to WebGL 1
        gl = canvas.getContext('webgl', { alpha: false });

        if (gl && gl instanceof WebGLRenderingContext) {
            return true;
        }

        // Last resort: experimental-webgl (old Safari, some mobile)
        gl = canvas.getContext('experimental-webgl', { alpha: false });

        return gl && gl instanceof WebGLRenderingContext;
    } catch {
        // WebGL detection failed (iframe restrictions, Electron, etc.)
        // Safe to return false - CPU processing will still work
        return false;
    }
}

/**
 * Get device capability tier based on AVAILABLE hardware metrics
 * Safely handles all environments: Web, Electron iframe, Mobile
 *
 * Checks:
 * - CPU cores (logical cores available)
 * - AVAILABLE memory (not just total)
 * - Current CPU usage (system load)
 * - GPU availability
 * - Battery status (mobile only)
 * - Thermal throttling (mobile only)
 *
 * @returns {string} One of DEVICE_CAPABILITY values
 */
export function getDeviceCapability() {
    // Safely get CPU cores (default to 4 if unavailable)
    const cpuCores = safeNavigatorGet('hardwareConcurrency', 4);

    // Get AVAILABLE memory (free, not total)
    const availableMemory = getAvailableMemory();

    // Get current CPU usage percentage (0-100)
    const currentCpuUsage = getEstimatedCpuUsage();

    // GPU detection with proper error handling
    const hasGpu = hasGraphicsAcceleration();

    // Check for thermal throttling (CPU is slower than baseline)
    const isThermallyThrottled = detectThermalThrottling(currentCpuUsage);

    // Apply CPU usage penalty
    // If CPU is already busy, downgrade capability
    let effectiveCpuCores = cpuCores;

    if (isThermallyThrottled || currentCpuUsage > 70) {
        // CPU is very busy or thermally throttled
        // Treat as having fewer cores available
        effectiveCpuCores = Math.max(1, Math.floor(cpuCores * 0.5));
    } else if (currentCpuUsage > 50) {
        // CPU is moderately busy
        effectiveCpuCores = Math.max(1, Math.floor(cpuCores * 0.7));
    }

    // Tier classification based on AVAILABLE resources
    // HIGH: Needs plenty of free resources
    if (effectiveCpuCores >= 8 && availableMemory >= 8 && hasGpu && currentCpuUsage < 30) {
        return DEVICE_CAPABILITY.HIGH;
    }

    // MEDIUM: Moderate resources
    if (effectiveCpuCores >= 4 && availableMemory >= 4 && hasGpu && currentCpuUsage < 50) {
        return DEVICE_CAPABILITY.MEDIUM;
    }

    // LOW: Limited resources but viable
    if (effectiveCpuCores >= 2 && availableMemory >= 2 && currentCpuUsage < 70) {
        return DEVICE_CAPABILITY.LOW;
    }

    // VERY_LOW: Minimal resources or high CPU usage
    return DEVICE_CAPABILITY.VERY_LOW;
}

/**
 * Get device capability tier WITH battery consideration
 * Use this for mobile devices where battery affects performance
 *
 * @returns {Promise<string>} One of DEVICE_CAPABILITY values
 */
export async function getDeviceCapabilityWithBattery() {
    // Start with base capability
    let capability = getDeviceCapability();

    try {
        // Check battery status (mobile only)
        const battery = await getBatteryStatus();

        if (battery) {
            // Critical: Low battery should force downgrade
            if (battery.level < 0.2) {
                // Battery critical (<20%) - force minimal performance
                logger.warn(
                    `[DeviceCapability] Critical battery level (${(battery.level * 100).toFixed(0)}%). `
                    + 'Downgrading to VERY_LOW.'
                );

                return DEVICE_CAPABILITY.VERY_LOW;
            }

            if (battery.level < 0.5 && !battery.charging) {
                // Low battery (<50%) and NOT charging - downgrade by one tier
                logger.info(
                    `[DeviceCapability] Low battery (${(battery.level * 100).toFixed(0)}%) `
                    + 'and not charging. Downgrading capability.'
                );

                if (capability === DEVICE_CAPABILITY.HIGH) {
                    capability = DEVICE_CAPABILITY.MEDIUM;
                } else if (capability === DEVICE_CAPABILITY.MEDIUM) {
                    capability = DEVICE_CAPABILITY.LOW;
                } else if (capability === DEVICE_CAPABILITY.LOW) {
                    capability = DEVICE_CAPABILITY.VERY_LOW;
                }
            }
        }
    } catch {
        // Battery API not available - continue with base capability
        logger.debug('[DeviceCapability] Battery status not available');
    }

    return capability;
}

/**
 * Detect slow network connection (mobile API)
 * Safe for all contexts; returns false if API unavailable (Web, Electron)
 *
 * @private
 * @returns {boolean} True if on slow connection (3G/4G)
 */
function isSlowNetworkConnection() {
    try {
        // Network Information API available only on mobile browsers
        const connection = safeNavigatorGet('connection', null)
            || safeNavigatorGet('mozConnection', null)
            || safeNavigatorGet('webkitConnection', null);

        if (!connection) {
            return false; // No network API available (Web/Desktop/Electron)
        }

        // Check effective connection type
        const effectiveType = connection.effectiveType;

        if (!effectiveType) {
            return false;
        }

        return effectiveType === '3g' || effectiveType === '4g';
    } catch {
        // Network detection failed; assume normal connection
        return false;
    }
}

/**
 * Get adaptive WASM loading timeout based on device capability and network
 * Safe for all contexts: Web, Electron iframe, Mobile browser
 *
 * @returns {number} Timeout in milliseconds (10000-60000)
 */
export function getAdaptiveWasmTimeout() {
    const capability = getDeviceCapability();
    const isSlowConnection = isSlowNetworkConnection();

    const baseTimeout = {
        [DEVICE_CAPABILITY.HIGH]: 10000,
        [DEVICE_CAPABILITY.MEDIUM]: 15000,
        [DEVICE_CAPABILITY.LOW]: 20000,
        [DEVICE_CAPABILITY.VERY_LOW]: 30000
    };

    let timeout = baseTimeout[capability] || 15000;

    // Add extra time for slow mobile connections
    if (isSlowConnection) {
        timeout *= 2;
    }

    return timeout;
}

/**
 * Get adaptive frame rate based on device capability
 *
 * @returns {number} Target frames per second (15, 24, or 30)
 */
export function getAdaptiveFrameRate() {
    const capability = getDeviceCapability();

    switch (capability) {
    case DEVICE_CAPABILITY.HIGH:
        return 30; // Full quality
    case DEVICE_CAPABILITY.MEDIUM:
        return 24; // Slight reduction
    case DEVICE_CAPABILITY.LOW:
        return 15; // Conservative
    case DEVICE_CAPABILITY.VERY_LOW:
        return 10; // Minimal
    default:
        return 15;
    }
}

/**
 * Get segmentation resolution based on device capability
 *
 * @returns {Object} Width and height for segmentation
 */
export function getSegmentationResolution() {
    // ⚠️ CRITICAL: Model requires exactly 256×144
    // The pre-trained selfie_segmentation_landscape.tflite model has FIXED input dimensions.
    // DO NOT VARY BASED ON DEVICE CAPABILITY - model will reject or produce poor results!
    //
    // Performance optimization happens AFTER segmentation:
    // 1. Feed 256×144 to model (always this size)
    // 2. Get segmentation mask (256×144)
    // 3. Downscale OUTPUT for rendering on LOW/VERY_LOW tiers
    // 4. Composite with background at scaled resolution
    //
    // See getQualitySettings() for per-tier output scaling factors
    return {
        width: 256,
        height: 144
    };
}

/**
 * Get quality settings based on device capability
 *
 * @param {string} [overrideCapability] - Override capability tier (for validated/downgraded tier)
 * @returns {Object} Quality settings object
 */
export function getQualitySettings(overrideCapability = null) {
    const capability = overrideCapability || getDeviceCapability();

    return {
        [DEVICE_CAPABILITY.HIGH]: {
            modelWidth: 256,
            modelHeight: 144,

            // Rendering output: No downscaling on high-end
            renderingScale: 1.0, // Output at full 256×144
            useGuidedFilter: true,
            guidedFilterRadius: 2,
            blurRadius: 2,
            enableTemporalSmoothing: true,
            temporalAlpha: 0.7, // Increased for smoother edges
            enableSmartZoom: true,
            enableVideoEnhancer: true
        },
        [DEVICE_CAPABILITY.MEDIUM]: {
            modelWidth: 256,
            modelHeight: 144,

            // Output downscaled to 75% for performance
            renderingScale: 0.75, // Output ~192×108
            useGuidedFilter: true,
            guidedFilterRadius: 2, // Increased from 1 for better edge-awareness
            blurRadius: 2, // Increased from 1 for smoother upscaling
            enableTemporalSmoothing: true,
            temporalAlpha: 0.6, // Increased from 0.5 for smoother edges
            enableSmartZoom: true,
            enableVideoEnhancer: true
        },
        [DEVICE_CAPABILITY.LOW]: {
            modelWidth: 256,
            modelHeight: 144,

            // Output downscaled to 62.5% for better performance
            renderingScale: 0.625, // Output ~160×90
            useGuidedFilter: true, // Enabled for better edge quality
            guidedFilterRadius: 1, // Smaller radius for performance
            blurRadius: 2, // Increased from 1 for smoother edges
            enableTemporalSmoothing: true,
            temporalAlpha: 0.5, // Increased from 0.4 for smoother edges
            enableSmartZoom: false,
            enableVideoEnhancer: false
        },
        [DEVICE_CAPABILITY.VERY_LOW]: {
            modelWidth: 256,
            modelHeight: 144,

            // Output downscaled to 50% for minimal processing
            renderingScale: 0.5, // Output ~128×72
            useGuidedFilter: true, // Enabled with minimal radius for edge quality
            guidedFilterRadius: 1, // Minimal but effective radius
            blurRadius: 1, // Minimum blur to hide upscaling artifacts
            enableTemporalSmoothing: true, // Enabled for frame-to-frame stability
            temporalAlpha: 0.4, // Conservative value for performance
            enableSmartZoom: false,
            enableVideoEnhancer: false
        }
    }[capability] || {
        modelWidth: 256,
        modelHeight: 144,
        renderingScale: 0.75,
        useGuidedFilter: true, // Enable guided filter for better edge quality
        guidedFilterRadius: 1, // Conservative radius for unknown devices
        blurRadius: 2, // Blur to hide upscaling artifacts
        enableTemporalSmoothing: true,
        temporalAlpha: 0.4,
        enableSmartZoom: false,
        enableVideoEnhancer: false
    };
}

/**
 * Get device capability information for logging/debugging
 * Safe for all contexts: Web, Electron iframe, Mobile browser
 *
 * @returns {Object} Detailed capability info
 */
export function getDeviceCapabilityInfo() {
    // Safely get total CPU cores (hardwareConcurrency unavailable in some contexts)
    const cpuCores = safeNavigatorGet('hardwareConcurrency', 'unknown');

    // Get AVAILABLE memory (free, not just total)
    const availableMemory = getAvailableMemory();
    const totalMemory = safeNavigatorGet('deviceMemory', 'unknown');

    // Get current CPU usage
    const cpuUsage = getEstimatedCpuUsage();

    // Check for thermal throttling
    const isThermallyThrottled = detectThermalThrottling(cpuUsage);

    // Safely get network connection type (mobile API only)
    let effectiveType = 'unknown';

    try {
        const connection = safeNavigatorGet('connection', null)
            || safeNavigatorGet('mozConnection', null)
            || safeNavigatorGet('webkitConnection', null);

        if (connection && connection.effectiveType) {
            effectiveType = connection.effectiveType;
        }
    } catch {
        // Network detection failed; keep default 'unknown'
    }

    const capability = getDeviceCapability();

    return {
        capability,

        // Hardware totals
        cpuCores,
        totalMemory,

        // Available resources (what matters!)
        availableMemory: availableMemory.toFixed(2),
        cpuUsagePercent: cpuUsage.toFixed(1),
        isThermallyThrottled,

        // Other metrics
        effectiveType,
        hasGpu: hasGraphicsAcceleration(),
        wasmTimeout: getAdaptiveWasmTimeout(),
        frameRate: getAdaptiveFrameRate(),
        segmentationResolution: getSegmentationResolution(),
        qualitySettings: getQualitySettings()
    };
}

/**
 * Check if device has sufficient memory for virtual background
 * Safe for all contexts; defaults to sufficient memory if unavailable (e.g., Electron)
 *
 * @returns {boolean} True if device likely has enough memory
 */
export function hasEnoughMemory() {
    // Get device memory with safe fallback to 8GB (assume sufficient)
    const deviceMemory = safeNavigatorGet('deviceMemory', 8);

    // Need at least 4GB for smooth operation
    // If deviceMemory is not a number (e.g., 'unknown'), assume sufficient
    if (typeof deviceMemory !== 'number') {
        return true;
    }

    return deviceMemory >= 4;
}

/**
 * Get user-friendly device tier description
 *
 * @returns {string} Description of device tier
 */
export function getDeviceDescription() {
    const capability = getDeviceCapability();
    const descriptions = {
        [DEVICE_CAPABILITY.HIGH]: 'High-performance device',
        [DEVICE_CAPABILITY.MEDIUM]: 'Standard device',
        [DEVICE_CAPABILITY.LOW]: 'Lower-end device',
        [DEVICE_CAPABILITY.VERY_LOW]: 'Very low-end device'
    };

    return descriptions[capability] || 'Unknown device';
}

/**
 * Format device capability info as human-readable string
 *
 * @returns {string} Formatted capability string
 */
export function formatDeviceCapabilityInfo() {
    const info = getDeviceCapabilityInfo();

    let formatted = `Device: ${getDeviceDescription()} | `
        + `CPU: ${info.cpuCores} cores (${info.cpuUsagePercent}% used`;

    // Add thermal throttling warning if detected
    if (info.isThermallyThrottled) {
        formatted += ', THERMALLY THROTTLED';
    }

    formatted += ') | '
        + `RAM: ${info.availableMemory}GB available (${info.totalMemory}GB total) | `
        + `GPU: ${info.hasGpu ? 'Yes' : 'No'} | `
        + `FPS Target: ${info.frameRate} | `
        + `Connection: ${info.effectiveType}`;

    return formatted;
}
